import type { ComputeUsage } from '../models/useUsage'
import type { BillingConsequences, CreditPosition, PlanRow } from '../models/useBilling'
import { autoReloadCanFire } from './credit.ts'
import { capitalize, formatCents } from './general.ts'

/**
 * The compute breakdown's display rules (app#136 item 3), kept out of the component so the two
 * that are easy to get quietly wrong can be tested.
 */

/**
 * The label for a row's shape.
 *
 * ⚠⚠ A NULL CLASS IS AN OFF-CATALOGUE SHAPE AND MUST NOT BE SNAPPED TO THE NEAREST CLASS.
 * `provision_node` took a caller-supplied cpu/memory pair before router#927, clamped to powers of
 * two, so rows exist whose shape matches no class. `docs/PRICING.md` § 4.2 requires they render as
 * unpriced. Naming them by their real shape says what ran; inventing a class name would put a
 * price beside a row that has none.
 */
export const computeClassLabel = (row: ComputeUsage): string =>
  row.class ?? `${row.cpu} CPU units · ${row.memoryMb} MB`

/**
 * Whether a row's cost is a real figure or an absence.
 *
 * ⚠⚠ UNPRICED IS NOT $0.00, AND THIS IS THE ONE RULE IN THIS FILE THAT COSTS TRUST TO GET WRONG.
 * An off-catalogue shape has no rate, so the router reports NULL cost; rendering that as "$0.00"
 * tells a customer we ran their task for free. They bill nothing, which is correct — router#927
 * stopped the API minting new ones — but the screen must say "unpriced", not "free".
 */
export const isPriced = (row: ComputeUsage): boolean => row.costCents !== null

/**
 * Whether this row's numbers are still moving.
 *
 * ⚠ A row with OPEN tasks reports a FLOOR, not a total: those tasks have not finished, so the
 * hours and the cost beside them will grow. A reader comparing the screen to an invoice has to
 * know which rows are still climbing, or the difference reads as an error in one of them.
 */
export const isClimbing = (row: ComputeUsage): boolean => row.openTasks > 0

/**
 * The org's compute allowance, read off any row.
 *
 * ⚠ The RPC repeats the org-level allowance on EVERY row, so reading it from the first is correct
 * and summing it would multiply it by the number of classes used. Returns null when there are no
 * rows at all — no usage means nothing to report, not an allowance of zero.
 */
export const computeAllowance = (
  rows: ComputeUsage[],
): { allowanceCents: number; remainingCents: number } | null => {
  const first = rows[0]
  // ⚠⚠ NULL IS THE EXEMPT TIER AND MUST NOT BECOME 0. The migration selects
  // `case when v_metered then v_allowance else null end`, so an unmetered org gets null here —
  // and "$0.00 included this period" is the opposite of what is true for a customer who is not
  // metered at all.
  if (!first || first.allowanceCents === null || first.allowanceRemainingCents === null) return null
  return { allowanceCents: first.allowanceCents, remainingCents: first.allowanceRemainingCents }
}

/**
 * Each row's cost in whole cents, for display, in the same order as `rows`.
 *
 * ⚠⚠ THE ROWS ADD UP TO THE ROUNDED EXACT SUM (app#327). The router reports `cost_cents` to four
 * decimal places, and the bar above prints the exact sum rounded once. When each row rounds on its
 * own, the rows drift from that figure by up to half a cent per row: three rows of 0.6¢ print
 * `$0.01` three times, a total of `$0.03`, under a bar that reads `$0.02`. So the whole cents of
 * `Math.round(exact sum)` are apportioned by the largest-remainder method: each row gets its
 * floor, and the cents left over go to the rows with the largest fractions. The rows then add up
 * to the Total row, and the Total row equals the bar.
 *
 * ⚠ Ties go to the earlier row, so the result does not depend on how `sort` orders equal keys.
 *
 * ⚠ Null stays null: an off-catalogue row is unpriced, never `$0.00`. See `isPriced`.
 */
export const apportionRowCents = (rows: readonly ComputeUsage[]): (number | null)[] => {
  const exact = rows.map(r => (r.costCents === null ? null : Math.max(0, r.costCents)))
  const cents = exact.map(c => (c === null ? null : Math.floor(c)))
  // ⚠ The sum is rounded to the router's four decimals before it is rounded to cents, so float
  // noise (2.4999999999999996 for 2.5) cannot move the target by a cent.
  const target = Math.round(Math.round(exact.reduce<number>((n, c) => n + (c ?? 0), 0) * 1e4) / 1e4)
  let left = target - cents.reduce<number>((n, c) => n + (c ?? 0), 0)
  const order = exact
    .flatMap((c, i) => (c === null ? [] : [{ i, frac: c - Math.floor(c) }]))
    .sort((a, b) => b.frac - a.frac || a.i - b.i)
  for (const { i } of order) {
    if (left <= 0) break
    cents[i] = (cents[i] ?? 0) + 1
    left--
  }
  return cents
}

/**
 * The totals under the table.
 *
 * ⚠⚠ AN UNPRICED ROW CONTRIBUTES NOTHING TO THE COST BY CONSTRUCTION — its cost is NULL — SO IT
 * IS COUNTED INSTEAD. `unpricedRows` is returned beside the total so the screen can say the total
 * does not cover everything that ran, which a bare number cannot.
 *
 * ⚠ `costCents` is the sum of `apportionRowCents` (whole cents), so it equals the sum of the
 * printed rows AND `Math.round(exactCents)`. `exactCents` also tells a true zero from a total
 * under half a cent.
 *
 * ⚠ `minutes` is BILLED minutes — what the router charges — and it counts unpriced rows too:
 * the work happened.
 */
export const computeTotals = (rows: ComputeUsage[]) => ({
  minutes: rows.reduce((n, r) => n + r.billedMinutes, 0),
  costCents: apportionRowCents(rows).reduce<number>((n, c) => n + (c ?? 0), 0),
  exactCents: rows.reduce((n, r) => n + (r.costCents ?? 0), 0),
  unpricedRows: rows.filter(r => !isPriced(r)).length,
  climbing: rows.some(isClimbing),
})

/**
 * Whole cents for display, with a floor for an amount that is real but under half a cent.
 *
 * ⚠ A task that ran two minutes on `small` costs 0.07¢. `formatCents` prints that as `$0.00`,
 * which says the task was free. `exactCents` is the unrounded figure; when it is above zero and
 * the rounded figure is zero, print `< $0.01`.
 */
export const formatCostCents = (cents: number, exactCents: number = cents): string =>
  cents === 0 && exactCents > 0 ? '< $0.01' : formatCents(cents)

/**
 * Billed minutes as `h m`, never as decimal hours (app#327).
 *
 * ⚠ `1.5 h` reads as one hour five minutes to many readers. `1 h 30 m` cannot be read wrong.
 * Minutes are zero-padded so the column lines up. The router bills whole minutes, so no seconds.
 */
const wholeHours = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

export const formatDuration = (minutes: number): string => {
  const m = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0
  return `${wholeHours.format(Math.floor(m / 60))} h ${String(m % 60).padStart(2, '0')} m`
}

/** The rows that ran something in the period. The RPC also returns every offered class at zero. */
export const usedComputeRows = (rows: ComputeUsage[]): ComputeUsage[] =>
  rows.filter(r => r.taskCount > 0 || r.billedMinutes > 0)

/**
 * How the used amount divides against the included amount (app#327).
 *
 * ⚠⚠ ON A PAID PLAN THE INCLUDED AMOUNT IS NOT A CEILING. Compute continues past it and is paid
 * from credit at the same rate. So this returns the two PARTS of what was used — the part the
 * plan included and the part past it — and never a fraction "of" the included amount. The paid
 * view draws the parts as two segments of one bar.
 *
 * ⚠⚠ `usedExactCents` IS THE COMPUTE AXIS'S `used`, THE NUMBER THE BAR ABOVE PRINTS, NOT A SUM
 * OF ROUNDED ROWS. The card and the bar state one figure for one quantity, so both start from
 * the same exact cents and round them once.
 *
 * ⚠ `leftCents` is the FLOOR of what is left, from the exact figure. 99.6¢ left prints `$0.99`,
 * never `$1.00`, so "left" and the hour hint built on it never promise money the pool does not
 * hold.
 *
 * ⚠ `overageCents` is the axis's `overage_cents`, the figure the line under the bar prints as
 * "from credit". When it is given, the part past the included amount IS that figure, so the card
 * and the bar cannot state two different amounts from credit. The router rounds it UP, so the two
 * parts can then add up to one cent more than `usedCents`. When it is not given, the part past the
 * included amount is `usedCents - includedUsedCents`.
 *
 * ⚠ `includedCents` null is the exempt tier: no pool, so no parts.
 */
export type ComputeSplit = {
  usedCents: number
  includedCents: number
  includedUsedCents: number
  pastIncludedCents: number
  leftCents: number
}

export const computeSplit = (
  usedExactCents: number,
  includedCents: number | null | undefined,
  overageCents?: number | null,
): ComputeSplit | null => {
  if (includedCents === null || includedCents === undefined || !Number.isFinite(includedCents)) return null
  // ⚠ Rounded to the router's four decimals first. The exact figure can be a float sum, and
  // `Math.floor(100 - 13.000000000000002)` is 86, one cent less than is left.
  const exact = Number.isFinite(usedExactCents) ? Math.max(0, Math.round(usedExactCents * 1e4) / 1e4) : 0
  const included = Math.max(0, Math.round(includedCents))
  const used = Math.round(exact)
  const includedUsed = Math.min(used, included)
  const past =
    typeof overageCents === 'number' && Number.isFinite(overageCents)
      ? Math.max(0, Math.round(overageCents))
      : used - includedUsed
  return {
    usedCents: used,
    includedCents: included,
    includedUsedCents: includedUsed,
    pastIncludedCents: past,
    leftCents: Math.max(0, Math.floor(included - exact)),
  }
}

/**
 * "about 43 h of a small machine left" — ONE hint that gives the money a feel (app#327).
 *
 * ⚠ The class and its rate come from the rows (`referenceComputeClass`), never from a constant.
 * The hours are rounded DOWN: "about 44 h" when 43.5 h are left promises time the budget does
 * not hold. Returns '' when nothing is left or there is no priced class, so the caller prints
 * nothing rather than "about 0 h".
 */
export const machineTimeLeftHint = (
  leftCents: number,
  reference: { name: string; centsPerHour: number } | null,
): string => {
  if (!reference || leftCents <= 0) return ''
  const hours = Math.floor(leftCents / reference.centsPerHour)
  const a = /^[aeiou]/i.test(reference.name) ? 'an' : 'a'
  return hours < 1
    ? `less than 1 h of ${a} ${reference.name} machine left`
    : `about ${wholeHours.format(hours)} h of ${a} ${reference.name} machine left`
}

/**
 * Whether a paid org's machines STOP once the included compute is used (app#327).
 *
 * ⚠⚠ ON A PAID PLAN COMPUTE CONTINUES FROM CREDIT, SO THIS IS THE EXCEPTION AND MUST BE SAID
 * PLAINLY. It is true when the org cannot pay from credit: spending from the balance is switched
 * off, or the balance is empty and no automatic refill can buy more (a $0 cap is the default,
 * and a spent cap is the same answer — `autoReloadCanFire`).
 *
 * ⚠ A pending purchase counts as credit: the customer has just paid.
 *
 * ⚠ Null credit is "cannot tell" (a Member, an older router, an arrears deployment) and answers
 * false: the screen must not claim a stop it cannot see. `creditBilled` false is the arrears
 * model, where usage is invoiced and nothing stops for lack of a balance.
 *
 * ⚠ `stopCompute` false means the router does not stop machines on this deployment, so the
 * sentence would be false. Null (older router) keeps the sentence, as `throttleConsequence` does.
 */
export const computeStopsWithoutCredit = (
  credit: CreditPosition | null,
  consequences: BillingConsequences | null,
): boolean => {
  if (!credit?.creditBilled) return false
  if (consequences && !consequences.stopCompute) return false
  if (!credit.creditEnabled) return true
  const hasCredit = (credit.balanceCents ?? 0) > 0 || (credit.pendingPurchaseCents ?? 0) > 0
  return !hasCredit && !autoReloadCanFire(credit)
}

/**
 * The plan a Free org is offered in the upsell line: the cheapest OFFERED paid plan (app#327).
 *
 * ⚠ Read from the catalogue, never named. "Pro includes $5.00" is two facts from `plans`, and a
 * literal would go stale on the next allowance change (app#331 changes it). Null when the
 * catalogue has not loaded or sells no paid plan with compute, so the line is not rendered.
 */
export const computeUpsellPlan = (plans: readonly PlanRow[]): PlanRow | null =>
  [...plans]
    .filter(p => p.metered && !p.hardCaps && p.offered !== false && p.computeAllowanceCents > 0)
    .sort((a, b) => a.monthlyFeeCents - b.monthlyFeeCents || a.name.localeCompare(b.name))[0] ?? null

/**
 * The upsell line under a Free org's compute budget.
 *
 * ⚠⚠ THE OFFERED PLAN'S AMOUNT IS A REASON ONLY WHEN IT IS MORE (Benoit, 2026-10-01). With the
 * launch promotion a Free org's budget is $11.00, and the line said "Pro includes $5.00 a month"
 * under "$0.01 of $11.00 free compute used": an offer of less than the reader has. The line
 * then keeps the one thing the plan changes, that compute continues from credit.
 *
 * ⚠ `includedCents` is the compute axis's `included`, the budget the card prints. Null (no pool)
 * and undefined (not loaded) give nothing to compare with, so the amount stays.
 *
 * ⚠ The comparison is with the ROUNDED budget, the figure the card prints. `included` can be a
 * fraction of a cent (router#1963: a grant gives what it holds), and "$5.00 free compute" above
 * "Pro includes $5.00 a month" is the same offer of nothing more.
 */
export const computeUpsellLine = (upsell: PlanRow, includedCents: number | null | undefined): string => {
  const name = capitalize(upsell.name)
  const more = includedCents == null || upsell.computeAllowanceCents > Math.round(includedCents)
  return more
    ? `${name} includes ${formatCents(upsell.computeAllowanceCents)} a month and continues from credit.`
    : `${name} continues from credit.`
}

/**
 * Whether the org ran ANY compute in the period.
 *
 * ⚠⚠ THE RPC RETURNS EVERY OFFERED CLASS AT ZERO WHEN UNUSED (`where cc.offered or u.task_count
 * is not null`), so a non-empty row list is NOT evidence that anything ran. Measured on prod
 * 2026-09-02: compute is 0.0000 cents fleet-wide, so without this the usage page would grow a
 * four-row table of zeroes for every organization.
 */
export const hasComputeUsage = (rows: ComputeUsage[]): boolean =>
  rows.some(r => r.taskCount > 0 || r.billedMinutes > 0)

/**
 * Whether the Compute card renders at all.
 *
 * ⚠⚠ AN EMPTY MONTH STILL SHOWS THE BUDGET (Benoit, 2026-09-14). A Free org that ran nothing
 * this month must read "$0.00 of $5.50 free compute used", not an absent card: the budget is the
 * fact it needs before it runs anything. The card used to return null when nothing ran, and that
 * rule applied to every plan, so a paid org now also sees "$0.00 used this month" with its
 * included amount.
 *
 * ⚠ `includedCents` is the compute axis's `included`, which already carries a promotion or support
 * grant. The empty-month card reads the same figure as a month with usage, so "$5.50" includes
 * the launch promotion in both.
 *
 * ⚠ With nothing used, the card needs a pool greater than zero to say anything. Null (the exempt
 * tier), undefined (not loaded) and 0 ("$0.00 of $0.00") render nothing, as before.
 *
 * ⚠⚠ NO ROWS IS NOT AN EMPTY MONTH. The RPC returns one row per OFFERED class even at zero usage,
 * so an empty list means the read failed or has not landed. The card must then render nothing:
 * "$0.00 used · No compute ran this month" under a bar that reads $2.40 is a false statement
 * about the customer's money. `useUsage` also sets `computeError` on a failed read, and the
 * component hides the card on it.
 */
export const showComputeCard = (rows: ComputeUsage[], includedCents: number | null | undefined): boolean =>
  rows.length > 0 &&
  (hasComputeUsage(rows) ||
    (typeof includedCents === 'number' && Number.isFinite(includedCents) && Math.round(includedCents) > 0))

/**
 * Whether the "machines stop" warning shows, and which remedy it names (app#327).
 *
 * ⚠⚠ THE RULE IS CONFIRMED BY BENOIT (2026-09-14) AND MUST NOT CHANGE: a paid plan, the current
 * month, a real included amount, nothing of it left, and no way to pay from credit
 * (`computeStopsWithoutCredit`).
 *
 * ⚠ The REMEDY depends on the cause. When paying from credit is switched off, a positive balance
 * still stops machines, so "add credit or raise your cap" names a remedy that does not work.
 * `'no-credit-spending'` is that cause; `'no-credit'` is an empty balance with no refill that can
 * fire. Null is no warning.
 */
export type ComputeStopWarning = 'no-credit-spending' | 'no-credit' | null

export const computeStopWarning = (args: {
  isPaid: boolean
  isCurrent: boolean
  split: ComputeSplit | null
  credit: CreditPosition | null
  consequences: BillingConsequences | null
}): ComputeStopWarning => {
  const { isPaid, isCurrent, split, credit, consequences } = args
  if (!isPaid || !isCurrent || !split || split.includedCents <= 0 || split.leftCents > 0) return null
  if (!computeStopsWithoutCredit(credit, consequences)) return null
  return credit?.creditEnabled === false ? 'no-credit-spending' : 'no-credit'
}

/**
 * The label of the included segment: "$1.00 included with Pro", or "$5.50 included".
 *
 * ⚠ The plan name shows only when the figure IS the plan's allowance. A promotion or a support
 * grant raises `included` above it, and then naming the plan would state a wrong allowance for
 * that plan. `UsageBoost` above the meters explains the extra.
 */
export const includedLabel = (includedCents: number, plan: PlanRow | null, planName: string | null): string =>
  plan && planName && Math.round(includedCents) === Math.round(plan.computeAllowanceCents)
    ? `${formatCents(includedCents)} included with ${capitalize(planName)}`
    : `${formatCents(includedCents)} included`

/**
 * The Free budget headline and its "left" figure (app#327).
 *
 * ⚠ The used amount goes through `formatCostCents`, so a month with no usage reads "$0.00" and a
 * month with 0.07¢ reads "< $0.01".
 */
export const freeComputeBudget = (split: ComputeSplit, exactCents: number): { headline: string; left: string } => ({
  headline: `${formatCostCents(split.usedCents, exactCents)} of ${formatCents(split.includedCents)} free compute used`,
  left: `${formatCents(split.leftCents)} left`,
})

/**
 * The class the "about N h of a small machine left" hint is stated in.
 *
 * ⚠⚠ THE USAGE PANEL READS MONEY, NOT HOURS (app#327, Benoit 2026-09-14). The allowance is one
 * dollar pool shared by every machine size, so the panel prints dollars, and this class gives
 * the one hint that turns the money into time. It no longer converts any figure on the panel.
 *
 * ⚠⚠ THE CLASS IS READ OFF THE ROWS, NEVER NAMED HERE. `get_org_compute_usage` returns a row per
 * OFFERED class with its rate even at zero usage (router migration 0111), so the catalogue is
 * already on the screen's own data. Hardcoding `'small'` would be a second copy of the catalogue
 * that can disagree with the rate printed beside it — and the rate is the half a customer checks.
 *
 * ⚠ The CHEAPEST priced class, which is what makes "read it off the rows" correct rather than
 * lucky: the catalogue prices ascend from `small` by construction ($0.06 per vCPU-hour from
 * `medium` up, each class doubling the last), so the cheapest row is the class the marketing copy
 * quotes, and it survives a rename or a re-price.
 *
 * ⚠ An off-catalogue row carries a NULL class and a NULL rate and is skipped — it prices nothing,
 * so it can convert nothing. A rate of 0 is skipped for the arithmetic's sake: it would divide.
 */
export const referenceComputeClass = (
  rows: readonly ComputeUsage[],
): { name: string; centsPerHour: number } | null => {
  const priced = rows.flatMap(r =>
    r.class !== null && r.priceCentsPerHour !== null && r.priceCentsPerHour > 0
      ? [{ name: r.class, centsPerHour: r.priceCentsPerHour }]
      : [],
  )
  if (priced.length === 0) return null
  // ⚠ Tie-broken by NAME. Two classes at one rate would otherwise make the label depend on the
  // order the RPC happened to return, which is a difference the reader cannot see or explain.
  return priced.sort((a, b) => a.centsPerHour - b.centsPerHour || a.name.localeCompare(b.name))[0]
}
